import { z } from "zod";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import FEATURE_FLAGS from "src/utils/constants/featureFlags";
import {
  EBillingCycleConfigurationType,
  EEntitledEmployeesType,
  EPresenceType,
  NotificationType,
  SubmitLimitationType,
  SubmitLimitationPeriodType,
  BillingCycle,
  Hierarchy,
  ApproversType,
} from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";

export const useAbsenceTypeSchema = () => {
  const hasabsencesVisibleForOthersEnabled = useFeatureIsOn(
    FEATURE_FLAGS.HIDDED_ABSENCE_TYPE
  );

  const submitLimitationSchema = z.object(
    {
      type: z.enum(SubmitLimitationType),
      count: z.number(),
      periodType: z.enum(SubmitLimitationPeriodType),
    },
    {
      error: "Ograniczenia złożenia wniosku są wymagane.",
    }
  );

  const configurationOptionsSchema = z.object({
    billInHours: z.boolean(),
    billInCalendarDays: z.boolean(),
    multipleRequestsPerDayAllowed: z.boolean(),
    modificationAllowed: z.boolean(),
    commentsAllowed: z.boolean(),
    justificationRequired: z.boolean(),
    attachmentRequired: z.boolean(),
    replacerRequired: z.boolean(),
    ...(hasabsencesVisibleForOthersEnabled
      ? { absencesVisibleForOthers: z.boolean() }
      : {}),
    submitLimitations: z.object({
      prior: submitLimitationSchema,
      advance: submitLimitationSchema,
    }),
  });

  return z
    .object({
      ownerId: z.string(),
      active: z.boolean(),
      name: z
        .string({
          message: "Nazwa jest wymagana",
        })
        .max(100, "Nazwa jest za długa, maksymalnie 100 znaków"),
      description: z.optional(
        z
          .string()
          .max(255, "Opis jest za długi, maksymalnie 255 znaków")
          .nullable()
      ),
      presenceType: z.enum(EPresenceType, {
        message: "Wybór jest wymagany",
      }),
      absenceGroupId: z.string({
        message: "Wybór grupy jest wymagany",
      }),
      hierarchy: z.enum(Hierarchy, { message: "Wybór jest wymagany" }),
      billingCycle: z.enum(BillingCycle, { message: "Wybór jest wymagany" }),
      billingCycleConfiguration: z
        .optional(
          z
            .object({
              type: z.optional(z.enum(EBillingCycleConfigurationType)),
              stackUnusedTimeForNextBillingCycle: z.optional(z.boolean()),
              parentAbsenceTypeId: z.optional(z.string().nullable()),
              patterns: z.optional(
                z.array(
                  z.object({
                    id: z.optional(z.string()),
                    value: z.coerce
                      .number({
                        message: "Wprowadź wartość liczbową.",
                      })
                      .min(1, "Wartość musi być większa od 0."),
                  })
                )
              ),
            })
            .superRefine((items, ctx) => {
              if (!items) return;
              const seen = new Map<number, number>(); // value → index
              const patterns = items.patterns || [];

              patterns.forEach((item, idx) => {
                if (seen.has(item.value)) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Wzorzec "${item.value}" jest już wykorzystany.`,
                    path: ["patterns", idx, "value"],
                  });
                } else {
                  seen.set(item.value, idx);
                }
              });
            })
        )
        .nullable(),
      entitledEmployees: z.object({
        type: z.enum(EEntitledEmployeesType, {
          message: "Wybór jest wymagany",
        }),
        employmentTypes: z.optional(z.array(z.string())),
        employeeGroups: z.optional(z.array(z.string())),
        organizationUnits: z.optional(z.array(z.string())),
        costPlaces: z.optional(z.array(z.string())),
      }),
      configurationOptions: configurationOptionsSchema,
      notificationsConfiguration: z.object({
        approverNotifications: z.object({
          absenceRequestCreation: z.array(z.enum(NotificationType)),
          absenceRequestEdition: z.array(z.enum(NotificationType)),
        }),
        immediateSuperiorAndApproverNotifications: z.object({
          differentApproverAbsenceRequestAcceptation: z.array(
            z.enum(NotificationType)
          ),
          differentApproverAbsenceRequestAutoAcceptation: z.array(
            z.enum(NotificationType)
          ),
        }),
        employeeNotifications: z.object({
          absenceRequestCreation: z.array(z.enum(NotificationType)),
          absenceRequestAcceptation: z.array(z.enum(NotificationType)),
          absenceRequestRejection: z.array(z.enum(NotificationType)),
          absenceRequestCancellation: z.array(z.enum(NotificationType)),
          absenceRequestSentForCorrection: z.array(z.enum(NotificationType)),
          absenceRequestEdition: z.array(z.enum(NotificationType)),
        }),
      }),
      approversType: z.enum(ApproversType, { message: "Wybór jest wymagany" }),
      approvers: z.optional(z.array(z.string())),
    })
    .refine(
      (val) =>
        val.approversType !== ApproversType.MANUAL ||
        (val.approvers && val.approvers.length > 0),
      {
        message: "Wybór przynajmniej jednej z opcji jest wymagany",
        path: ["approvers"],
      }
    );
};
